<script setup>
import { computed } from 'vue';

import { useNow } from '@vueuse/core';

import { useMarketplaceTimeZone } from '@/composables/useMarketplaceTimeZone.js';
import { zoneAbbrev } from '@/helpers/dateHelpers.js';

// IANA zone of the marketplace: its own, else the business profile's default, else UTC
const marketplaceTimezone = useMarketplaceTimeZone();
// The same instant drives the clock and its daylight-saving-aware zone label.
const now = useNow({ interval: 1000 * 10 });

const marketplaceTimezoneLabel = computed(() => zoneAbbrev(marketplaceTimezone.value, now.value));

// Helper to determine if locale uses 12h or 24h format
function getHour12ByLocale(locale = navigator.language) {
  // Use resolvedOptions to check hourCycle
  const dtf = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
  const options = dtf.resolvedOptions();
  // hourCycle: 'h23' or 'h24' means 24h, 'h11' or 'h12' means 12h
  if (options.hourCycle === 'h23' || options.hourCycle === 'h24') return false;
  if (options.hourCycle === 'h11' || options.hourCycle === 'h12') return true;
  // fallback: if hour12 is present
  if (typeof options.hour12 === 'boolean') return options.hour12;
  // fallback: use en-US (12h) and most others (24h)
  return locale.startsWith('en');
}

const browserLocale = navigator.language || 'en-US';

const timeString = computed(() => {
  try {
    const date = new Date(now.value);
    // Use Intl.DateTimeFormat for timezone and locale
    const formatter = new Intl.DateTimeFormat(browserLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: getHour12ByLocale(browserLocale),
      timeZone: marketplaceTimezone.value,
    });
    return formatter.format(date);
  } catch (e) {
    return '--:--';
  }
});
</script>

<template>
  <div class="marketplace-clock">
    <span class="marketplace-clock__time" :title="marketplaceTimezone">
      <span>{{ timeString }}</span>
      <span class="marketplace-clock__tz">{{ marketplaceTimezoneLabel }}</span>
    </span>
  </div>
</template>

<style scoped lang="scss">
.marketplace-clock {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-text, #222);
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column-reverse;
    gap: 0;
  }

  &__time {
    display: inline-flex;
    align-items: baseline;
    gap: 0.4em;
    white-space: nowrap;
    font-family: var(--wp-font-clock);
    font-size: 22px;
    background: #181818;
    color: #39ff14;
    padding: 2px 10px 2px 10px;
    border-radius: 6px;
    letter-spacing: 0.08em;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    min-width: 60px;
    text-align: center;
    @media (max-width: 768px) {
      font-size: 18px;
      font-weight: 400;
      min-width: 50px;
      padding: 2px 4px 2px 4px;
    }
  }
}
</style>
